import { useState, useEffect, useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { WebsiteContext } from "@/pages/website/WebsiteProvider";
import { totalSelector } from "@/features/order/slice";
import { isLoggedInSelector } from "@/features/auth/slice";
import routes from "@/constants/routes";

import { Door01Icon, GiftIcon, Invoice01Icon, ServingFoodIcon, UserCircle02Icon } from "@hugeicons/core-free-icons";
import useTerminology from "./useTerminology";

export interface MenuItemConfig {
  id: string;
  label: string;
  icon: any;
  type: "lucide" | "hugeicons";
  to?: string;
  onClick?: () => void;
  badge?: boolean;
}

export const useMenuNavigation = () => {
  const { organizationAlias } = useParams<{ organizationAlias: string }>();
  const { organization } = useContext(WebsiteContext);
  const total = useSelector(totalSelector);
  const isLoggedIn = useSelector(isLoggedInSelector);
  const { t } = useTerminology();
  const navigate = useNavigate();

  const isLoyaltyEnabled = organization?.settings?.loyaltyProgram?.isEnabled;

  const [activeMenuItem, setActiveMenuItem] = useState<number>(0);
  const [openOrder, setOpenOrder] = useState(false);

  const handleOpenOrder = () => {
    setOpenOrder((prev) => !prev);
  };

  // Generate paths
  const paths = {
    home: organizationAlias ? routes.organizations.root.replace(":organizationAlias", organizationAlias) : routes.home,
    menus: organizationAlias ? routes.organizations.menu.main.replace(":organizationAlias", organizationAlias) : routes.menus,
    premios: organizationAlias ? routes.organizations.loyaltyPublic.main.replace(":organizationAlias", organizationAlias) : routes.loyalty,
    perfil: organizationAlias ? routes.organizations.customerProfile.main.replace(":organizationAlias", organizationAlias) : routes.customerProfile,
  };

  // Compile standard items list
  const menuItems: MenuItemConfig[] = [
    { id: "inicio", label: "Inicio", icon: Door01Icon, type: "hugeicons" as const, to: paths.home },
    { id: "menus", label: t("catalog", 2, true), icon: ServingFoodIcon, type: "hugeicons" as const, to: paths.menus },
    ...(isLoyaltyEnabled
      ? [
        {
          id: "premios",
          label: "Premios",
          icon: GiftIcon,
          type: "hugeicons" as const,
          to: paths.premios,
        },
      ]
      : []),
    {
      id: "cuenta",
      label: "Cuenta",
      icon: Invoice01Icon,
      type: "hugeicons" as const,
      badge: !!total,
      onClick: handleOpenOrder,
    },
    {
      id: "perfil",
      label: "Mi Perfil",
      icon: UserCircle02Icon,
      type: "hugeicons" as const,
      to: paths.perfil,
    },
  ];

  useEffect(() => {
    const currentPath = window.location.pathname;
    let foundIndex = -1;

    if (currentPath.includes("/loyalty") && isLoyaltyEnabled) {
      foundIndex = menuItems.findIndex((item) => item.id === "premios");
    } else if (currentPath.includes("/perfil")) {
      foundIndex = menuItems.findIndex((item) => item.id === "perfil");
    } else if (currentPath.includes("/menus") || currentPath.includes("/orders")) {
      foundIndex = menuItems.findIndex((item) => item.id === "menus");
    } else {
      // Default to "inicio" if we're on the root path
      foundIndex = menuItems.findIndex((item) => item.id === "inicio");
    }

    if (foundIndex !== -1) {
      setActiveMenuItem(foundIndex);
    }
  }, [window.location.pathname, isLoyaltyEnabled, isLoggedIn]);

  const navigateToHome = () => {
    if (organizationAlias) {
      navigate(routes.organizations.root.replace(":organizationAlias", organizationAlias));
    } else {
      navigate(routes.home);
    }
  };

  return {
    organization,
    organizationAlias,
    isLoyaltyEnabled,
    isLoggedIn,
    total,
    activeMenuItem,
    setActiveMenuItem,
    openOrder,
    setOpenOrder,
    handleOpenOrder,
    paths,
    menuItems,
    navigateToHome,
  };
};

export default useMenuNavigation;
